import { useEffect, useRef, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import OpenInBrowserIllustration from './OpenInBrowserIllustration';
import CopyLinkField from './CopyLinkField';
import ExternalBrowserGuideModal from './ExternalBrowserGuideModal';
import '../css/FacebookBrowserModal.css';
import {
  openInExternalBrowser,
  stripTrackingParams,
  watchExternalBrowserHandoff,
} from '../utils/inAppBrowserUtil';
import {
  FACEBOOK_BROWSER_MESSAGE,
  FACEBOOK_BROWSER_MODAL_TITLE,
  FACEBOOK_BROWSER_HELP_TITLE,
  FACEBOOK_BROWSER_HELP_TEXT_BEFORE,
  FACEBOOK_BROWSER_HELP_LINK,
  FACEBOOK_BROWSER_HELP_TEXT_AFTER,
  FACEBOOK_BROWSER_OPEN_LABEL,
} from '../constants/bookingConst';

/**
 * Version 2 of the "Open in your browser" popup (Config -> Theme -> Open in
 * Browser Popup), asking a guest inside Facebook's (or Messenger's) built-in
 * browser to reopen the site in their phone's default browser. When and
 * whether it shows is decided by FacebookBrowserPrompt; this renders it.
 *
 * Layout, top to bottom: the app-to-browser illustration, the title, the
 * message, a tinted help card (a link to the manual-steps guide, plus the
 * page link to copy), and a full-width "Open in Browser" button that hands
 * the page to the device's browser on the site's clean URL (fbclid removed).
 *
 * If the button's hand-off does not happen — Facebook's iOS browser can
 * refuse it — the "Open in Browser Manually" guide pops up on its own. The
 * help card opens the same guide on request.
 *
 * Deliberately NOT closable — no × button and no dismissal on a tap outside
 * the card (an exception to the app-wide modal rule, by request): booking
 * features are unreliable in Facebook's webview, so the only way forward is
 * the real browser.
 *
 * Preview mode (the UI preview page and Config -> Theme's preview) adds a ×
 * so it can be dismissed, and makes "Open in Browser" act as if the hand-off
 * failed — it opens the guide instead of leaving the page.
 *
 * @param {object} [props]
 * @param {boolean} [props.preview=false] - render for a preview screen
 * @param {string} [props.appName] - preview only: 'Facebook' or 'Messenger' for the guide
 * @param {() => void} [props.onClose] - preview only: closes the popup
 * @returns {JSX.Element}
 */
function FacebookBrowserModal({ preview = false, appName, onClose } = {}) {
  // The page URL is read once: it does not change while the popup is up.
  const [pageUrl] = useState(() => stripTrackingParams(window.location.href));
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const stopHandoffWatchRef = useRef(null);

  // Stops a pending hand-off watch if the popup goes away first.
  useEffect(() => () => stopHandoffWatchRef.current?.(), []);

  /** Opens the manual-steps guide over this popup. */
  const openGuide = () => setIsGuideOpen(true);

  /**
   * Opens the guide from the keyboard (Enter / Space), as a button would.
   *
   * @param {React.KeyboardEvent<HTMLSpanElement>} event
   * @returns {void}
   */
  const handleGuideLinkKey = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    openGuide();
  };

  /**
   * Tries the automatic hand-off, and pops up the guide if the page is still
   * here afterwards. The watch is armed first so it sees the hand-off begin.
   * In preview it skips the hand-off and shows the failed-hand-off result.
   *
   * @returns {void}
   */
  const handleOpen = () => {
    if (preview) {
      openGuide();
      return;
    }
    stopHandoffWatchRef.current?.();
    stopHandoffWatchRef.current = watchExternalBrowserHandoff(openGuide);
    openInExternalBrowser();
  };

  return (
    // No onClick on the overlay, and a × only in preview: for guests this
    // popup is not closable.
    <div className="modal-overlay fbm-overlay">
      <div className="fbm-card" role="dialog" aria-modal="true" aria-labelledby="fbm-title">
        {preview && onClose && (
          <button type="button" className="modal-close fbm-close" onClick={onClose} aria-label="Close">×</button>
        )}
        <OpenInBrowserIllustration />

        <h2 id="fbm-title" className="fbm-title">{FACEBOOK_BROWSER_MODAL_TITLE}</h2>
        <p className="fbm-message">{FACEBOOK_BROWSER_MESSAGE}</p>

        <div className="fbm-help">
          <strong className="fbm-help-title">{FACEBOOK_BROWSER_HELP_TITLE}</strong>
          <p className="fbm-help-text">
            {FACEBOOK_BROWSER_HELP_TEXT_BEFORE}
            {/* A span rather than a <button> so the phrase wraps with the
                sentence like ordinary text; role/tabIndex/keys keep it a
                proper button for keyboards and screen readers. */}
            <span
              role="button"
              tabIndex={0}
              className="fbm-help-link"
              onClick={openGuide}
              onKeyDown={handleGuideLinkKey}
            >
              {FACEBOOK_BROWSER_HELP_LINK}
            </span>
            {FACEBOOK_BROWSER_HELP_TEXT_AFTER}
          </p>
          <CopyLinkField url={pageUrl} />
        </div>

        <button type="button" className="fbm-open" onClick={handleOpen}>
          <ExternalLink size={18} strokeWidth={2.2} aria-hidden="true" />
          {FACEBOOK_BROWSER_OPEN_LABEL}
        </button>
      </div>

      {isGuideOpen && <ExternalBrowserGuideModal appName={appName} onClose={() => setIsGuideOpen(false)} />}
    </div>
  );
}

export default FacebookBrowserModal;
